import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';

import { SlideData } from '../../../../../containers/Editor/utils/indesignEditor/editableSliders/getData';
import {
	Wrapper,
	IconMove,
	PreviewWrapper,
	IconType,
	Preview,
	Label,
	ActionsWrapper,
	Button,
} from './styles';
import Portal from '../../../../Portal';
import ProgressBar from './ProgressBar';
import { FileError } from '../../../../../containers/ImportManager/actions';
import { Api } from '../../../../../typings/api';
import Utils from '../../../../../utils';

interface SlideProps {
	page: number;
	sliderId: string;
	index: number;
	item: SlideData;
	previewUrl: string;
	isImporting: boolean;
	importPercentage: number;
	importError: FileError | undefined;
	isDragDisabled: boolean;
	onEdit: (index: number) => void;
	onRemove: (id: string, index: number) => void;
}

interface PortalAwareItemProps extends SlideProps {
	provided: DraggableProvided;
	snapshot: DraggableStateSnapshot;
}

const getSlideIconName = (type: Api.AssetItemFileTypes, isExternalLink: boolean): string => {
	if (isExternalLink) {
		return 'iconset_youtube_logo';
	}

	return type === Api.AssetItemFileTypes.IMAGE
		? 'iconset_gallery-bold'
		: 'iconset_uprez_video_rounded';
};

const PortalAwareItem = ({
	index,
	item: { id, type, label, isExternalLink, slideText },
	previewUrl,
	isImporting,
	importPercentage,
	importError,
	isDragDisabled,
	onEdit,
	onRemove,
	provided,
	snapshot: { isDragging },
}: PortalAwareItemProps) => {
	const item = (
		<Wrapper
			ref={provided.innerRef}
			{...provided.draggableProps}
			isDragging={isDragging}
		>
			{!isDragDisabled &&
				<IconMove {...provided.dragHandleProps}>
					{Utils.getSvgIcon('iconset_6dots')}
				</IconMove>
			}
			<PreviewWrapper>
				<IconType hasText={slideText && slideText.value}>
					{Utils.getSvgIcon(getSlideIconName(type, isExternalLink))}
				</IconType>
				{isImporting || importError
					? <ProgressBar percentage={importPercentage} error={importError} />
					: <Preview src={previewUrl} />
				}
			</PreviewWrapper>
			<Label>
				{isExternalLink
					? <Translate value="messages.youtube_link" />
					: label
				}
			</Label>
			{!isDragging &&
				<ActionsWrapper>
					{!isImporting && !importError &&
						<Button onClick={() => onEdit(index)}>
							{Utils.getSvgIcon('iconset_uprez_write_bold')}
						</Button>
					}
					<Button
						onClick={() => onRemove(id, index)}
						isRemoveButton={true}
					>
						{Utils.getSvgIcon('iconset_close-2')}
					</Button>
				</ActionsWrapper>
			}
		</Wrapper>
	);

	if (isDragging) {
		return (
			<Portal parentNode={document.body}>{item}</Portal>
		);
	}

	return item;
};

const Slide = (props: SlideProps) => (
	<Draggable
		draggableId={props.index.toString()}
		index={props.index}
		isDragDisabled={props.isDragDisabled}
	>
		{(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
			<PortalAwareItem
				{...props}
				provided={provided}
				snapshot={snapshot}
			/>
		)}
	</Draggable>
);

export default React.memo(Slide);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/SliderManager/Slide/index.tsx